import { useEffect } from "react";
import { HS_GAIL_ACCOUNT_EMAIL_KEY } from "@/app/(onboarding)/constants";
import type { CompanyInfo } from "@/app/api/company-lookup/route";
import { useAuthContext, type User } from "@/contexts/common/AuthProvider";
import {
  useUserOrganizations,
  type UserOrg,
} from "@/contexts/common/UserOrganizationsProvider";
import { Address, Org, OrgCreate, Owner } from "@/types/org";
import { sanitizeAddress } from "@/utils/common/address";
import { GET_GAIL_PATH, PUBLIC_API_ROUTES } from "@/utils/common/constants/routes";
import { useRouter } from "@bprogress/next";
import { getOrganizationNameFallback } from "@/utils/onboarding/orgName";
import { normalizeLocalization } from "@/constants/localization";

/** sessionStorage key for the Stripe payload written before switchOrg. */
export const PENDING_PAYMENT_STORAGE_KEY = "pendingPaymentData";

/**
 * After a self-serve org is created, land on /get-gail so the stored
 * trial checkout payload can start Stripe provisioning. Switching into
 * `/` skips that step and leaves the org without products.
 */
export const POST_ACCOUNT_CREATE_RETURN_TO = GET_GAIL_PATH;

export function hasPendingPaymentData(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(sessionStorage.getItem(PENDING_PAYMENT_STORAGE_KEY));
  } catch {
    return false;
  }
}

export function getOnboardingRoutingState(
  user: User | undefined,
  orgs: UserOrg[]
) {
  const hasSelectedOrg = !!user?.orgId;
  const hasAgencyId = !!user?.agencyId;
  const hasAuth0OrgMembership = orgs.length > 0;

  return {
    needsOrgSelection:
      !!user &&
      ((!hasAgencyId && hasAuth0OrgMembership) ||
        (hasAgencyId && !hasSelectedOrg)),
    userNeedsOnboarding:
      !!user && !hasAgencyId && !hasAuth0OrgMembership,
  };
}

/**
 * Creates a function that, when called, waits until at least `minMs`
 * milliseconds have elapsed since the enforcer was created.
 *
 * Useful for ensuring loading animations play for a minimum duration
 * even when the underlying operation completes quickly.
 *
 * @example
 * ```ts
 * const enforce = createMinDurationEnforcer(20_000);
 * await doSomeWork();
 * await enforce();          // waits for the remainder of 20 s
 * ```
 */
export function createMinDurationEnforcer(minMs: number) {
  const startedAt = Date.now();
  return async () => {
    const remaining = minMs - (Date.now() - startedAt);
    if (remaining > 0) await new Promise((r) => setTimeout(r, remaining));
  };
}

export function useOnboardingRouting() {
  const router = useRouter();
  const { user } = useAuthContext();
  const { orgs, isLoading: isOrgsLoading } = useUserOrganizations();

  const { needsOrgSelection, userNeedsOnboarding } =
    getOnboardingRoutingState(user, orgs);

  useEffect(() => {
    if (user && !isOrgsLoading) {
      // If user has no orgs at all, they need onboarding - don't redirect to springboard
      if (orgs.length === 0 && userNeedsOnboarding) {
        return;
      }

      if (needsOrgSelection) {
        router.push("/springboard");
        return;
      }

      if (userNeedsOnboarding) {
        return;
      }

      if (!userNeedsOnboarding) {
        router.push("/");
        return;
      }
    }
  }, [user, userNeedsOnboarding, needsOrgSelection, orgs, isOrgsLoading]);

  return { needsOrgSelection, userNeedsOnboarding };
}

async function updateAuth0UserName(firstName: string, lastName: string) {
  try {
    // With cookie-based auth, credentials are sent automatically
    await fetch("/api/user/update-name", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        given_name: firstName,
        family_name: lastName,
      }),
    });
  } catch (error) {
    console.error("Error updating Auth0 user name:", error);
  }
}

export async function updateName(firstName: string, lastName: string, currentUserEmail: string) {
  const hsAccountEmail = globalThis.localStorage.getItem(HS_GAIL_ACCOUNT_EMAIL_KEY);

  try {
    // Update CRM systems (HubSpot)
    await fetch(PUBLIC_API_ROUTES.SETUP_AGENCY_UPDATE_CONTACT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        type: "name",
        firstName: firstName,
        lastName: lastName,
        hubspotContactEmail: hsAccountEmail ?? currentUserEmail,
      }),
    });

    // Update Auth0 user profile
    await updateAuth0UserName(firstName, lastName);
  } catch (error) {
    console.error(error);
  }
}

export async function updateContactInfo(
  businessInfo: { name: string; owner: Owner },
  currentUserEmail: string
) {
  const hsAccountEmail = globalThis.localStorage.getItem(HS_GAIL_ACCOUNT_EMAIL_KEY);

  try {
    await fetch(PUBLIC_API_ROUTES.SETUP_AGENCY_UPDATE_CONTACT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        type: "details",
        agencyName: businessInfo.name,
        email: businessInfo.owner.email,
        phone: businessInfo.owner.phoneNumber,
        hubspotContactEmail: hsAccountEmail ?? currentUserEmail,
      }),
    });
  } catch (error) {
    console.error(error);
  }
}

export type CreateOrganizationResult =
  | { status: "ok"; org: Org }
  | { status: "conflict" };

export async function createOrganization(
  businessInfo: { name: string; owner: Owner },
  agencyAddress: Address | undefined,
  website: string,
  firstName: string,
  lastName: string,
  companyInfo?: CompanyInfo
): Promise<CreateOrganizationResult> {
  const organizationName = getOrganizationNameFallback({
    currentName: businessInfo.name,
    companyName: companyInfo?.companyName,
  });

  const newBusinessInfo: OrgCreate = {
    name: organizationName,
    address: sanitizeAddress(agencyAddress) ?? null,
    owner: {
      firstName: firstName,
      lastName: lastName,
      email: businessInfo.owner.email,
      phoneNumber: businessInfo.owner.phoneNumber,
    },
    localization: normalizeLocalization(
      navigator.languages && navigator.languages.length > 0 ? navigator.languages[0] : "en-US"
    ),
    details: {
      website,
      ...(companyInfo && {
        description: companyInfo.businessDescription,
        industries: [companyInfo.industryClassification],
        services: companyInfo.servicesOffered,
        industryTag: companyInfo.industryTag,
        keyProducts: companyInfo.keyProducts,
        targetMarket: companyInfo.targetMarket,
        gailCanDo: companyInfo.gailCanDo,
        companySize: companyInfo.companySize,
        headquarters: companyInfo.headquarters,
        scrapedContacts: companyInfo.contacts,
        businessHours: companyInfo.businessHours ?? undefined,
        scrapedFaqs: companyInfo.keyFaqs,
      }),
    },
  };

  const response = await fetch("/api/public/onboarding/setup-agency", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(newBusinessInfo),
  });

  if (response.status === 409) {
    return { status: "conflict" };
  }

  if (!response.ok) {
    throw new Error("Failed to create organization");
  }

  const org = (await response.json()) as Org | null;
  if (!org) {
    throw new Error("Failed to create organization");
  }

  return { status: "ok", org };
}
